import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import GroupIcon from "@material-ui/icons/Group";
import { useTranslation } from "react-i18next";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import { Button } from "@material-ui/core";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { toast } from "utils";
import { checkSupplierCode, createSupplier, modifySupplier } from "../../redux/actions/suppliers";
import Financial from "./components/Financial";
import Contact from "./components/Contact";
import Identification from "./components/Identification";
import GeneralInformation from "./components/GeneralInformation";
import useStyles from "./style";
import { fetchSupplier } from "../../redux/actions/invoices";
import ConfirmDialog from "../../components/ConfirmDialog";

const AddSupplier = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const dispatch = useDispatch();
	const history = useHistory();
	const { idSupplier } = useParams();
	const getSteps = () => [
		t("supplier.add.step1"),
		t("supplier.add.step2"),
		t("supplier.add.step3"),
		t("supplier.add.step4"),
	];
	const [activeStep, setActiveStep] = useState(0);
	const [supplier, setSupplier] = React.useState({ contacts: [] });
	const [contact, setContact] = React.useState({});
	const [logo, setLogo] = React.useState(null);
	const steps = getSteps();
	const { errors, handleSubmit, register } = useForm();
	const [isEdit, setIsEdit] = React.useState(false);
	const [confirmObject, setConfirmObject] = React.useState({
		message: "",
		openConfirm: false,
		cancel: false,
		isLoading: false,
	});

	const supplierToUpdate = useSelector(({ requests }) =>
		get(requests.queries, "FETCH_SUPPLIER.data")
	);

	const updateSupplier = (e) => {
		const { name, value } = e.target;
		setSupplier((prevState) => ({ ...prevState, [name]: value }));
	};

	const updateManySupplier = (data) => {
		setSupplier({ ...supplier, ...data });
	};

	const updateSupplierContact = (e) => {
		setContact({ ...contact, [e.target.name]: e.target.value });
	};

	const handleBack = () => {
		if (activeStep === 0) history.goBack();
		checkSupplierCode(
			supplier,
			(rep) => {
				if (!rep.data) {
					setActiveStep((prevActiveStep) => prevActiveStep - 1);
				} else {
					toast.error(t("supplier.name.exist.already"));
				}
			},
			() => {
				// bad request
				toast.error(t("supplier_error"));
			}
		);
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		setActiveStep(0);
	};

	const onSubmit = () => {
		if (!idSupplier) {
			// check supplier's name in the first step of the form
			dispatch(
				checkSupplierCode(
					supplier,
					(rep) => {
						if (!rep.data) {
							setActiveStep((prevActiveStep) => prevActiveStep + 1);
							if (activeStep === 3) {
								setConfirmObject({ message: t("supplier.add.confirm"), openConfirm: true });
							}
						} else {
							toast.error(t("supplier.name.exist.already"));
						}
					},
					() => {
						// bad request
						toast.error(t("supplier_error"));
					}
				)
			);
		} else {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
			if (activeStep === 3) {
				setConfirmObject({ message: t("supplier.add.confirm"), openConfirm: true });
			}
		}
	};

	const getStepContent = (stepIndex) => {
		switch (stepIndex) {
			case 0:
				return (
					<Identification
						register={register}
						errors={errors}
						updateSupplier={updateSupplier}
						setSupplier={setSupplier}
						supplier={supplier}
						updateManySupplier={updateManySupplier}
						edit={isEdit}
					/>
				);
			case 1:
				return (
					<GeneralInformation
						register={register}
						errors={errors}
						updateSupplier={updateSupplier}
						updateManySupplier={updateManySupplier}
						supplier={supplier}
						setLogo={setLogo}
					/>
				);
			case 2:
				return (
					<Financial
						register={register}
						errors={errors}
						updateSupplier={updateSupplier}
						supplier={supplier}
					/>
				);

			case 3:
				return (
					<Contact
						register={register}
						errors={errors}
						updateSupplier={updateSupplier}
						supplier={supplier}
						contact={contact}
						updateSupplierContact={updateSupplierContact}
					/>
				);
			default:
				return "Unknown stepIndex";
		}
	};

	useEffect(() => {
		if (idSupplier) {
			dispatch(fetchSupplier(idSupplier)).then((res) => {
				if (res.status === 200) {
					setIsEdit(true);
					setSupplier(res.data);
					if (supplierToUpdate?.contacts && supplierToUpdate?.contacts.length > 0) {
						setContact(supplierToUpdate?.contacts[0]);
					}
				}
			});
		}
	}, []);

	useEffect(() => {
		if (idSupplier && supplierToUpdate && Number(idSupplier) !== supplierToUpdate.id) {
			dispatch(fetchSupplier(idSupplier));
			setIsEdit(true);
			setSupplier(supplierToUpdate);
			if (supplierToUpdate?.contacts && supplierToUpdate?.contacts.length > 0) {
				setContact(supplierToUpdate?.contacts[0]);
			}
		}
	}, [supplierToUpdate, idSupplier]);

	const onCancel = () => {
		setConfirmObject({ message: "", openConfirm: false, isLoading: false });
		setActiveStep(activeStep - 1);
	};

	const onConfirm = () => {
		setConfirmObject({ ...confirmObject, isLoading: true });
		supplier.countryCode = supplier.countryCode || "FR";
		supplier.currency = supplier.currency || "EUR";
		if (!isEmpty(contact)) {
			if (idSupplier) {
				supplier.contacts[0] = contact;
			} else {
				supplier.contacts.push(contact);
			}
		}
		setSupplier({ ...supplier });
		if (idSupplier) {
			dispatch(modifySupplier(supplier, logo)).then((res) => {
				if (res.action.response?.status) {
					setConfirmObject({ message: "", openConfirm: false });
					toast.success(t("supplier_updated"));
					history.push(`/supplier`);
				} else {
					onCancel();
					switch (res.error?.response.status) {
						case 403:
							toast.error(t("logoInfected"), { autoClose: 3000 });
							break;
						case 503:
							toast.error(t("logoScanFailed"), { autoClose: 3000 });
							break;
						default:
							toast.error(t("supplier_error"));
							break;
					}
				}
			});
		} else {
			dispatch(createSupplier(supplier, logo)).then((res) => {
				if (res.action.response?.status) {
					setConfirmObject({ message: "", openConfirm: false });
					toast.success(t("supplier_created"));
					history.push(`/supplier`);
				} else {
					onCancel();
					toast.error(t("supplier_error"));
					if (res.error?.response.status === 403 || res.error?.response.status === 503) {
						toast.error(t(get(res, "error.response.data.detail")), { autoClose: 3000 });
					} else {
						toast.error(t("supplier_error"));
					}
				}
			});
		}
	};

	return (
		<Grid container xs={12} justify="center" alignContent="center" alignItems="center">
			<Grid container item className={classes.headerPath} direction="row">
				<Grid container direction="row" justify="flex-start" spacing={1}>
					<Grid item>
						<GroupIcon fontSize="small" />
					</Grid>
					<Grid item className={classes.headerBreadCumb}>
						<Typography variant="body2" color="primary">
							{t("manageUsers")} {" > "}{" "}
							{idSupplier ? t("supplier.modify") : t("supplier.add2.button")}
						</Typography>
					</Grid>
				</Grid>
			</Grid>
			<Grid container xs={12} justify="center">
				<div className={classes.root}>
					<Stepper activeStep={activeStep} alternativeLabel>
						{steps.map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
					<div>
						{activeStep === steps.length ? (
							<div>
								<Typography className={classes.instructions}>All steps completed</Typography>
								<Button onClick={handleReset}>Reset</Button>
							</div>
						) : (
							<div>
								<form onSubmit={handleSubmit(onSubmit)}>
									<Grid
										container
										item
										xs={12}
										justify="center"
										className={classes.formContainer}
										style={{ padding: "20px" }}
									>
										{getStepContent(activeStep)}
									</Grid>
									<Grid container justify="center" style={{ marginTop: "50px" }}>
										<Button variant="outlined" onClick={handleBack} className={classes.backButton}>
											{activeStep === 0 ? t("cancel") : t("supplier.add.previous")}
										</Button>
										<Button variant="contained" color="secondary" type="submit">
											{activeStep === steps.length - 1
												? t("supplier.add.finish")
												: t("supplier.add.next")}
										</Button>
									</Grid>
								</form>
							</div>
						)}
					</div>
				</div>
			</Grid>
			<ConfirmDialog
				message={confirmObject.message}
				open={confirmObject.openConfirm}
				isLoading={confirmObject.isLoading}
				onConfirm={onConfirm}
				onCancel={onCancel}
			/>
		</Grid>
	);
};

export default AddSupplier;
